import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Github, Facebook, Instagram, Youtube, ArrowRight, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { companyInfo } from '../mock';
import logo from './assets/logo.png';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-[rgba(10,37,64,0.95)] to-[#0a2540] border-t-2 border-[#00a8ff] mt-12 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00a8ff] rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#4cd3ff] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="container mx-auto px-6 py-16 relative z-10">
        {/* Top Section - Newsletter */}
        <div className="mb-16 pb-16 border-b-2 border-[#00a8ff]">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl md:text-4xl font-bold led-text mb-4 animate-led-pulse" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
              STAY UPDATED WITH LED INNOVATIONS
            </h3>
            <p className="text-[#00a8ff] mb-8" style={{ textShadow: '0 0 8px #00a8ff' }}>
              Subscribe to our newsletter for the latest tech insights and exclusive offers
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
              <div className="relative flex-1">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#00a8ff]" size={20} />
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  className="pl-12 h-14 bg-[rgba(10,37,64,0.7)] border-2 border-[#00a8ff] text-white rounded-xl focus:ring-2 focus:ring-[#4cd3ff]"
                  style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}
                />
              </div>
              <Button className="h-14 px-8 bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold text-lg transition-all duration-300 hover:scale-105" style={{ boxShadow: '0 0 20px #00a8ff' }}>
                <Send className="mr-2" size={20} />
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img
                src={logo}
                alt="Max Bien Logo"
                className="h-12 animate-pulse-scale"
                style={{ filter: 'brightness(0) saturate(100%) invert(61%) sepia(98%) saturate(3180%) hue-rotate(175deg) brightness(103%) contrast(101%) drop-shadow(0 0 15px #00a8ff)' }}
              />
              <div className="text-2xl font-bold led-text tracking-wider animate-led-pulse">
                MAX <span className="font-black">BIEN</span>
              </div>
            </div>
            <p className="text-white mb-6 text-lg leading-relaxed" style={{ textShadow: '0 0 5px #00a8ff' }}>
              {companyInfo.tagline}
            </p>
            <p className="text-[#00a8ff] mb-6" style={{ textShadow: '0 0 5px #00a8ff' }}>
              {companyInfo.description}
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { icon: Linkedin, url: 'https://linkedin.com', color: '#0077b5' },
                { icon: Twitter, url: 'https://twitter.com', color: '#1da1f2' },
                { icon: Github, url: 'https://github.com', color: '#333' },
                { icon: Facebook, url: 'https://facebook.com', color: '#1877f2' },
                { icon: Instagram, url: 'https://instagram.com', color: '#e4405f' },
                { icon: Youtube, url: 'https://youtube.com', color: '#ff0000' }
              ].map((social, idx) => (
                <a
                  key={idx}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-[rgba(0,168,255,0.2)] border-2 border-[#00a8ff] flex items-center justify-center text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] transition-all duration-300 hover:scale-125 hover:rotate-12"
                  style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-6 led-text relative inline-block" style={{ textShadow: '0 0 10px #00a8ff' }}>
              QUICK LINKS
              <span className="absolute -bottom-2 left-0 w-16 h-1 bg-[#00a8ff]" style={{ boxShadow: '0 0 10px #00a8ff' }}></span>
            </h3>
            <ul className="space-y-3">
              {['About Us', 'Services', 'Portfolio', 'Case Studies', 'Careers', 'Blog'].map((item, idx) => (
                <li key={idx}>
                  <Link
                    to={`/${item.toLowerCase().replace(' ', '-')}`}
                    className="text-[#00a8ff] hover:text-white hover:pl-3 transition-all duration-300 flex items-center group"
                    style={{ textShadow: '0 0 5px #00a8ff' }}
                  >
                    <ArrowRight size={16} className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Services */}
          <div>
            <h3 className="text-xl font-bold mb-6 led-text relative inline-block" style={{ textShadow: '0 0 10px #00a8ff' }}>
              OUR SERVICES
              <span className="absolute -bottom-2 left-0 w-16 h-1 bg-[#00a8ff]" style={{ boxShadow: '0 0 10px #00a8ff' }}></span>
            </h3>
            <ul className="space-y-3">
              {['Web Development', 'Mobile Apps', 'Cloud Solutions', 'AI Integration', 'Cybersecurity', 'UI/UX Design'].map((service, idx) => (
                <li key={idx} className="text-[#00a8ff] flex items-start" style={{ textShadow: '0 0 5px #00a8ff' }}>
                  <span className="mr-2 text-[#4cd3ff]">•</span>
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-6 led-text relative inline-block" style={{ textShadow: '0 0 10px #00a8ff' }}>
              CONTACT INFO
              <span className="absolute -bottom-2 left-0 w-16 h-1 bg-[#00a8ff]" style={{ boxShadow: '0 0 10px #00a8ff' }}></span>
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 group hover:pl-2 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-[rgba(0,168,255,0.2)] flex items-center justify-center flex-shrink-0 group-hover:bg-[#00a8ff] group-hover:scale-110 transition-all" style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}>
                  <Mail size={18} className="text-[#00a8ff] group-hover:text-[#0a2540]" />
                </div>
                <div>
                  <p className="text-xs text-[#4cd3ff] mb-1">Email Us</p>
                  <a href={`mailto:${companyInfo.email}`} className="text-white hover:text-[#00a8ff] transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                    {companyInfo.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 group hover:pl-2 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-[rgba(0,168,255,0.2)] flex items-center justify-center flex-shrink-0 group-hover:bg-[#00a8ff] group-hover:scale-110 transition-all" style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}>
                  <Phone size={18} className="text-[#00a8ff] group-hover:text-[#0a2540]" />
                </div>
                <div>
                  <p className="text-xs text-[#4cd3ff] mb-1">Call Us</p>
                  <a href={`tel:${companyInfo.phone}`} className="text-white hover:text-[#00a8ff] transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                    {companyInfo.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3 group hover:pl-2 transition-all duration-300">
                <div className="w-10 h-10 rounded-lg bg-[rgba(0,168,255,0.2)] flex items-center justify-center flex-shrink-0 group-hover:bg-[#00a8ff] group-hover:scale-110 transition-all" style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}>
                  <MapPin size={18} className="text-[#00a8ff] group-hover:text-[#0a2540]" />
                </div>
                <div>
                  <p className="text-xs text-[#4cd3ff] mb-1">Visit Us</p>
                  <p className="text-white" style={{ textShadow: '0 0 5px #00a8ff' }}>
                    {companyInfo.address}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t-2 border-[#00a8ff] pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white text-center md:text-left" style={{ textShadow: '0 0 8px #00a8ff' }}>
                          &copy; {currentYear} <span className="font-bold led-text">Max Bien Software</span>. All rights reserved. | Designed with passion at Max Bien.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-[#00a8ff] hover:text-white transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-[#00a8ff] hover:text-white transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                Terms of Service
              </Link>
              <Link to="/sitemap" className="text-[#00a8ff] hover:text-white transition-colors" style={{ textShadow: '0 0 5px #00a8ff' }}>
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom LED Strip Effect */}
      <div className="h-2 bg-gradient-to-r from-transparent via-[#00a8ff] to-transparent animate-pulse" style={{ boxShadow: '0 0 20px #00a8ff' }}></div>
    </footer>
  );
};